import { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { calculateAllKpis, type SimulationResults, type SystemConfig } from '../utils/financialCalculations';
import type { FinancialParams } from '../utils/financialDefaults';

export default function BreakEvenChart({ sim, sys, fp, degradation, printMode }: { sim: SimulationResults; sys: SystemConfig; fp: FinancialParams; degradation: number; printMode?: boolean }) {
  const { data, breakEvenTariff } = useMemo(() => {
    const points: { tariff: number; npv: number }[] = [];
    for (let t = 0.10; t <= 0.50; t += 0.01) {
      const rounded = Math.round(t * 100) / 100;
      const { kpis } = calculateAllKpis(sim, sys, { ...fp, chargingTariff: rounded }, degradation);
      points.push({ tariff: rounded, npv: kpis.npv });
    }
    let be: number | null = null;
    for (let i = 1; i < points.length; i++) {
      if (points[i - 1].npv < 0 && points[i].npv >= 0) {
        const frac = Math.abs(points[i - 1].npv) / (Math.abs(points[i - 1].npv) + points[i].npv);
        be = points[i - 1].tariff + frac * (points[i].tariff - points[i - 1].tariff);
        break;
      }
    }
    return { data: points, breakEvenTariff: be };
  }, [sim, sys, fp, degradation]);

  const fmt = (v: number) => `€${(v / 1e6).toFixed(2)}M`;
  return (
    <div className="bg-white rounded-xl border border-[#D9E2EC] shadow-sm p-4 mt-4">
      <div className="flex justify-between items-start mb-3">
        <div>
          <div className="text-xs font-bold text-[#0B1F4A]">📈 Break-Even Analyse</div>
          <div className="text-[10px] text-[#8899AA]">NPV als functie van het laadtarief</div>
        </div>
        {breakEvenTariff !== null && (
          <div className="text-right">
            <div className="text-[10px] text-[#8899AA]">Break-even tarief</div>
            <div className="text-sm font-bold text-[#10B981]">€{breakEvenTariff.toFixed(3)}/kWh</div>
          </div>
        )}
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={data} margin={{ top: 10, right: 20, bottom: 20, left: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
          <XAxis dataKey="tariff" type="number" domain={[0.10, 0.50]} tickFormatter={v => `€${v.toFixed(2)}`} tick={{ fontSize: 10, fill: '#8899AA' }} label={{ value: 'Laadtarief (€/kWh)', position: 'bottom', offset: 5, style: { fontSize: 10, fill: '#8899AA' } }} />
          <YAxis tickFormatter={v => `€${(v / 1e6).toFixed(1)}M`} tick={{ fontSize: 10, fill: '#8899AA' }} />
          <Tooltip formatter={(value: number) => [fmt(value), 'NPV']} labelFormatter={(label: number) => `Tarief: €${label.toFixed(2)}/kWh`} contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #D9E2EC' }} />
          <ReferenceLine y={0} stroke="#94A3B8" strokeDasharray="6 3" />
          {breakEvenTariff !== null && <ReferenceLine x={breakEvenTariff} stroke="#10B981" strokeDasharray="4 4" strokeWidth={1.5} />}
          <Line type="monotone" dataKey="npv" stroke="#00A3C4" strokeWidth={2} dot={false} activeDot={{ r: 4 }} isAnimationActive={!printMode} />
          <ReferenceLine x={fp.chargingTariff} stroke="#EF4444" strokeDasharray="3 3" strokeWidth={1.5} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
